import React, { createContext, useContext, useRef, useState, ReactNode, RefObject } from 'react'
import { useWebsocket } from './websocket'

interface MediaSourceContextValue {
  mediaSource: RefObject<MediaSource | null>
  mediaSourceUrl: string | null
  createMediaSource: (mimeCodec?: string) => void
  resetMediaSource: (mimeCodec?: string) => void
  isSourceOpen: boolean
  // Flips true once the SourceBuffer has finished appending its first
  // segment, i.e. there's actually something to play — the signal AVPlayer
  // waits for before calling video.play() itself, instead of relying on the
  // <video autoPlay> attribute to notice new data on its own.
  bufferReady: boolean
}

const MediaSourceContext = createContext<MediaSourceContextValue | undefined>(undefined)

const posKey = 'pos'
const playKey = 'play'
const timeKey = 'time'
const clockKey = 'clock'

// Fallback used until a preset-specific codec is known (e.g. before the
// presets list has loaded), matching what used to be hardcoded here.
const DEFAULT_MIME_CODEC = 'video/mp4; codecs="avc1.420034, mp4a.40.2"'

export const MediaSourceProvider = ({ children }: { children: ReactNode }) => {
  const { registerWebsocketMessaging } = useWebsocket()

  const buffer = useRef<SourceBuffer | null>(null)
  const queue = useRef<ArrayBuffer[]>([])

  const unregisterWebsocket = useRef<(() => void) | undefined>(undefined)
  const mediaSource = useRef<MediaSource | null>(
    typeof window !== 'undefined' && 'MediaSource' in window ? new MediaSource() : null
  )

  const [isSourceOpen, setIsSourceOpen] = useState(false)
  const [bufferReady, setBufferReady] = useState(false)

  const [mediaSourceUrl, setMediaSourceUrl] = useState<string | null>(() =>
    mediaSource.current ? URL.createObjectURL(mediaSource.current) : null
  )

  // The codec last used to open a SourceBuffer, so a codec-less reset (e.g.
  // the server's 'clearbuffer' command, which just wants a fresh buffer for
  // the same stream) keeps using it instead of falling back to the default.
  const currentMimeCodec = useRef<string>(DEFAULT_MIME_CODEC)

  const resetMediaSource = (mimeCodec?: string) => {
    console.log('RESET MEDIA SOURCE')

    setIsSourceOpen(false)
    setBufferReady(false)

    if (unregisterWebsocket.current) {
      console.log('UNREGISTER WEBSOCKET LISTENER FROM BUFFER')
      unregisterWebsocket.current()
    }

    buffer.current = null
    queue.current = []

    if (mediaSourceUrl) {
      URL.revokeObjectURL(mediaSourceUrl)
    }

    console.log('CREATE NEW MEDIA SOURCE')
    const newMediaSource =
      typeof window !== 'undefined' && 'MediaSource' in window ? new MediaSource() : null
    mediaSource.current = newMediaSource
    const newUrl = newMediaSource ? URL.createObjectURL(newMediaSource) : null
    setMediaSourceUrl(newUrl)

    console.log('NEUE MEDIA SOURCE URL: ' + mediaSourceUrl)

    /*if (videoRef.current) {
      videoRef.current.src = mediaSourceUrl
    }*/
    createMediaSource(mimeCodec)
  }

  const createMediaSource = (mimeCodec?: string) => {
    if (mimeCodec) currentMimeCodec.current = mimeCodec

    // Captured so the listener below always opens a buffer on the instance
    // it was actually attached to, not whatever `mediaSource.current` points
    // to by the time the (async) 'sourceopen' event fires — a fast channel
    // switch can call resetMediaSource again before this one ever opens,
    // moving the ref on to a newer, still-closed MediaSource.
    const targetMediaSource = mediaSource.current

    if (targetMediaSource) {
      targetMediaSource.addEventListener(
        'sourceopen',
        function () {
          if (mediaSource.current !== targetMediaSource) {
            console.log('sourceopen fired for a superseded MediaSource, ignoring')
            return
          }

          console.log('sourceopen')
          setIsSourceOpen(true)
          buffer.current = targetMediaSource.addSourceBuffer(currentMimeCodec.current)
          buffer.current.mode = 'segments'

          buffer.current.addEventListener('update', function () {
            if (queue.current.length > 0 && !buffer.current!.updating) {
              buffer.current!.appendBuffer(queue.current.shift()!)
            }
          })

          buffer.current.addEventListener(
            'updateend',
            function () {
              // First segment is in buffer — enough to start playback.
              setBufferReady(true)
            },
            { once: true }
          )

          buffer.current.addEventListener('error', function () {
            console.log('An error occured')
          })

          unregisterWebsocket.current = registerWebsocketMessaging(messageHandler)
        },
        { once: true }
      )

      function messageHandler(message: MessageEvent) {
        if (typeof message.data === 'string') {
          if (message.data.startsWith(posKey)) {
            console.log('POS TRACKED')
            console.log(message.data.split(posKey)[1])
            //mediaSource.duration = message.data.split(posKey)[1]
            //videoRef.current.pause()
            //videoRef.current.currentTime = message.data.split(posKey)[1]
          } else if (message.data.startsWith(playKey)) {
            console.log('START TRACKED')
            //videoRef.current.play()
            //startTime = Date.now() + (buffer.buffered.end(0) - videoRef.current.currentTime)
            //console.log('START TIME: ' + startTime)
          } else if (message.data.startsWith(timeKey)) {
            console.log('TIME TRACKED')
            console.log(message.data.split(timeKey)[1])
          } else if (message.data.startsWith(clockKey)) {
            console.log('CLOCK UPDATE')
            console.log('CURRENT SERVER CLOCK: ' + message.data.split(clockKey)[1])
          } else if (message.data === 'clearbuffer') {
            /*buffer.current.remove(0, videoRef.current.currentTime - 2)
            videoRef.current.play()*/

            console.log('BUFFER CLEARED CMD')
            resetMediaSource()
          } else if (message.data === 'header') {
            console.log('HEADER RECEIVED')
            console.log(message.data)
          }
        } else if (typeof message.data === 'object') {
          const payload: ArrayBuffer = message.data

          if (!buffer.current || mediaSource.current!.readyState !== 'open') {
            queue.current.push(payload)
            return
          }

          if (buffer.current.updating || queue.current.length > 0) {
            queue.current.push(payload)
          } else {
            try {
              buffer.current.appendBuffer(payload)
            } catch (err) {
              console.error('Fehler beim AppendBuffer:', err)
            }
          }
        }
      }
    }
  }

  return (
    <MediaSourceContext
      value={{
        mediaSource,
        mediaSourceUrl,
        createMediaSource,
        resetMediaSource,
        isSourceOpen,
        bufferReady,
      }}
    >
      {children}
    </MediaSourceContext>
  )
}

export const useMediaSource = (): MediaSourceContextValue => {
  const context = useContext(MediaSourceContext)
  if (!context) {
    throw new Error('useMediaSourceContext must be used within an MediaSourceProvider')
  }
  return context
}
